import { useAuthStore } from './stores/auth.store'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:6300/api/v1'
const REFRESH_FAILURE_COOLDOWN_MS = 30_000
const REFRESH_LOCK_NAME = 'neuroforge:auth:refresh-cookie'
// A 409 is a CAS loser, not evidence of a stolen cookie. The winning response
// from another app origin must be allowed to install its Set-Cookie first.
const REFRESH_CONTENTION_RETRIES = 3
const REFRESH_CONTENTION_DELAY_MS = 350

let refreshInFlight: Promise<boolean> | null = null
let refreshBlockedUntil = 0
let blockedEpoch = -1

/** Snapshot of the session identity/token revision; no token is persisted to disk. */
export function currentSessionSnapshot(): {
  epoch: number
  token: string | null
  userId: string | null
} {
  const state = useAuthStore.getState()
  return { epoch: state.sessionEpoch, token: state.accessToken, userId: state.user?.id ?? null }
}

export function isSessionSnapshotCurrent(
  snapshot: ReturnType<typeof currentSessionSnapshot>,
): boolean {
  const now = useAuthStore.getState()
  return now.sessionEpoch === snapshot.epoch && now.accessToken === snapshot.token
}

/**
 * The only /auth/refresh writer in shared/client. Callers (bootstrap, 401 retry)
 * share this promise and the same session epoch/cooldown. Web Locks serialize
 * refreshes across same-origin tabs (but cannot coordinate different app origins).
 * A missing Web Locks API is an explicitly documented release-gate limitation.
 */
export function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  if (useAuthStore.getState().authPending) return Promise.resolve(false)
  if (Date.now() < refreshBlockedUntil && useAuthStore.getState().sessionEpoch === blockedEpoch) {
    return Promise.resolve(false)
  }

  const snapshot = currentSessionSnapshot()
  const performRefresh = async (): Promise<boolean> => {
    if (!isSessionSnapshotCurrent(snapshot) || useAuthStore.getState().authPending) return false

    try {
      let response: Response | undefined
      for (let attempt = 0; attempt <= REFRESH_CONTENTION_RETRIES; attempt++) {
        if (!isSessionSnapshotCurrent(snapshot) || useAuthStore.getState().authPending) return false
        response = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          cache: 'no-store',
          redirect: 'error',
          referrerPolicy: 'no-referrer',
        })
        if (!isSessionSnapshotCurrent(snapshot)) return false
        if (response.status !== 409) break
        // No token issued on conflict. The winner will set the shared cookie;
        // do not clear local authentication, and never busy-loop or retry forever.
        if (attempt === REFRESH_CONTENTION_RETRIES) return false
        await new Promise<void>((resolve) =>
          setTimeout(resolve, REFRESH_CONTENTION_DELAY_MS * (attempt + 1)),
        )
      }
      if (!response) return false

      if (response.status === 401 || response.status === 403) {
        useAuthStore.getState().logout()
        blockedEpoch = useAuthStore.getState().sessionEpoch
        refreshBlockedUntil = Date.now() + REFRESH_FAILURE_COOLDOWN_MS
        return false
      }
      if (!response.ok) return false

      const payload: unknown = await response.json()
      if (!isSessionSnapshotCurrent(snapshot)) return false
      if (
        !payload ||
        typeof payload !== 'object' ||
        !('success' in payload) ||
        payload.success !== true ||
        !('data' in payload) ||
        !payload.data ||
        typeof payload.data !== 'object' ||
        !('accessToken' in payload.data) ||
        typeof payload.data.accessToken !== 'string' ||
        !payload.data.accessToken
      ) {
        return false
      }

      refreshBlockedUntil = 0
      blockedEpoch = -1
      useAuthStore.getState().setAccessToken(payload.data.accessToken)
      return true
    } catch {
      // Network failure is not evidence that the user's session was revoked.
      return false
    }
  }

  const run = async (): Promise<boolean> => {
    const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
    if (locks?.request) {
      // In a waiting tab, the cookie is read only AFTER the preceding tab's
      // Set-Cookie has been applied and the lock released.
      return locks.request(REFRESH_LOCK_NAME, { mode: 'exclusive' }, performRefresh)
    }
    // Safe intra-tab single-flight, but NOT cross-tab: fail release QA for
    // browsers lacking Web Locks when refresh cookie is shared by tabs.
    return performRefresh()
  }

  const promise = run()
    .catch(() => false)
    .finally(() => {
      if (refreshInFlight === promise) refreshInFlight = null
    })
  refreshInFlight = promise
  return promise
}
